import React, { useState, ChangeEvent, FormEvent, useRef } from 'react';
import { Navbar, Container, Button, Form, Table, Alert, Spinner, Collapse } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

type Absence = {
  platiDo: string;
  osoba: string;
  cisMzdSloz: string;
  platiOd: string;
};

type ResultItem = {
  rowIndex: number;
  id: string;
  success: boolean;
  payload: {
    winstrom: {
      nepritomnost: Absence[];
      '@version': string;
    };
  };
  errorMessage: string | null;
};

type AppErrorItem = {
  uuid: string;
  timestamp: string;
  errorLevel: string;
  httpStatusCode: string;
  message: string;
  trace: string;
};

type ExpectedDtoOut = {
  appErrors: {
    itemList: AppErrorItem[];
  };
  resultList: ResultItem[];
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [responseData, setResponseData] = useState<ExpectedDtoOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openRows, setOpenRows] = useState<{ [key: number]: boolean }>({});

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLogout = () => {
    sessionStorage.removeItem('sessionToken');
    navigate('/login');
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Prosím vyberte XLSX soubor.');
      return;
    }

    setUploading(true);
    setError(null);
    setResponseData(null);

    const token = sessionStorage.getItem('sessionToken');
    if (!token) {
      setError('Uživatelský token není dostupný. Přihlaste se znovu.');
      setUploading(false);
      navigate('/login');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('https://flexiimport.upi-group.cz/import/absence', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        let errorMessages;
        try {
          const errorJson = await response.json();
          if (errorJson.appErrors?.itemList?.length > 0) {
            const messages: string[] = errorJson.appErrors.itemList.map((item: AppErrorItem) => item.message);
            errorMessages = messages.join(', ');
          } else {
            errorMessages = await response.text();
          }
        } catch (parseError) {
          errorMessages = await response.text();
        }
        setError(`Chyba při nahrávání souboru: ${errorMessages}`);
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        setUploading(false);
        return;
      }

      const data: ExpectedDtoOut = await response.json();
      setResponseData(data);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error(err);
      setError('Došlo k chybě při nahrávání souboru.');
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setUploading(false);
    }
  };

  const toggleRow = (rowIndex: number) => {
    setOpenRows(prev => ({ ...prev, [rowIndex]: !prev[rowIndex] }));
  };

  const renderTable = () => {
    if (!responseData) return null;

    return (
        <div className="mt-4">
          <h3>Výsledky importu</h3>

          {responseData.appErrors.itemList.length > 0 && (
              <Alert variant="danger">
                <Alert.Heading>Chyby aplikace</Alert.Heading>
                <ul>
                  {responseData.appErrors.itemList.map((errorItem, index) => (
                      <li key={index}>{errorItem.message}</li>
                  ))}
                </ul>
              </Alert>
          )}

          <Table striped bordered hover responsive>
            <thead>
            <tr>
              <th>Index</th>
              <th>Flexi ID</th>
              <th>Status</th>
              <th>Payload</th>
              <th>Chybový Log</th>
            </tr>
            </thead>
            <tbody>
            {responseData.resultList.map((result, index) => (
                <React.Fragment key={index}>
                  <tr>
                    <td className="align-middle text-center">{result.rowIndex}</td>
                    <td className="align-middle text-center">{result.id}</td>
                    <td className="align-middle">
                      {result.success ? (
                          <span className="text-success">Importováno</span>
                      ) : (
                          <span className="text-danger">Chyba</span>
                      )}
                    </td>
                    <td className="align-middle">
                      <Button
                          variant="link"
                          onClick={() => toggleRow(index)}
                          aria-controls={`collapse-row-${index}`}
                          aria-expanded={openRows[index] || false}
                          className="p-0"
                      >
                        {openRows[index] ? 'Skrýt' : 'Zobrazit'}
                      </Button>
                    </td>
                    <td className="align-middle">{result.errorMessage || 'Žádná'}</td>
                  </tr>
                  <tr>
                    <td colSpan={5} className="p-0">
                      <Collapse in={openRows[index]}>
                        <div id={`collapse-row-${index}`} className="p-3 bg-light">
                          <h5>Payload</h5>
                          <pre>{JSON.stringify(result.payload, null, 2)}</pre>
                        </div>
                      </Collapse>
                    </td>
                  </tr>
                </React.Fragment>
            ))}
            </tbody>
          </Table>
        </div>
    );
  };

  return (
      <div>
        <Navbar bg="light" expand="lg" className="mb-4">
          <Container className="my-custom-container">
            <Navbar.Brand>Absence Import</Navbar.Brand>
            <Navbar.Collapse className="justify-content-end">
              <Button variant="outline-danger" onClick={handleLogout}>
                Odhlásit se
              </Button>
            </Navbar.Collapse>
          </Container>
        </Navbar>

        <Container className="my-custom-container">
          <h2>Nahrát XLSX soubor</h2>
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="formFile" className="mb-3">
              <Form.Label>Vyberte XLSX soubor</Form.Label>
              <Form.Control
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileChange}
                  required
                  ref={fileInputRef} // Přidání ref
              />
            </Form.Group>
            <Button variant="primary" type="submit" disabled={uploading}>
              {uploading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    {' '}Nahrávám...
                  </>
              ) : (
                  'Nahrát'
              )}
            </Button>
          </Form>

          {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
          {renderTable()}
        </Container>
      </div>
  );
};

export default Dashboard;
